import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, CircularProgress, IconButton, LinearProgress, Skeleton, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowSharpIcon from '@mui/icons-material/PlayArrowSharp';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { PiSoundcloudLogoDuotone } from "react-icons/pi";
import Bullet from '../components/Bullet';
import { NowPlayingContext } from '../components/NowPlayingProvider';
import { SIDENAV_WIDTH } from '../components/LeftSidebar';
import useRetryOnReconnect from '../components/useRetryOnReconnect';

const token = process.env.REACT_APP_WSSR_API_TOKEN;

const formatDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
};

const FeedTrackList = () => {
  const { directory } = useParams();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [loadingTrackId, setLoadingTrackId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [buffer, setBuffer] = useState(10);
  const [totalDuration, setTotalDuration] = useState(0);
  const [trackCount, setTrackCount] = useState(0);
  const [scale, setScale] = useState(1);
  const [topImage, setTopImage] = useState(null);
  const revealObserver = useRef(null);

  const {
    playSpecificTrack,
    currentTrackId,
    isPlaying,
    setIsPlaying,
    nowPlayingData,
  } = useContext(NowPlayingContext);

  const fetchTracks = async () => {
    setLoading(true);
    try {
      const endpoint = `https://broadcast.wssr.stream/api/station/1/files/list?sort=timestamp&sortOrder=DESC&currentDirectory=${encodeURIComponent(directory)}`;
      const response = await axios.get(endpoint, { headers: { Authorization: token } });

      const coverTrack = response.data.find(track => track.path_short === 'cover.png');
      let topImageUrl = null;

      if (coverTrack && coverTrack.links?.download) {
        try {
          const imageResponse = await axios.get(
            `https://broadcast.wssr.stream${coverTrack.links.download}`,
            {
              headers: { Authorization: token },
              responseType: 'blob'
            }
          );
          topImageUrl = URL.createObjectURL(imageResponse.data); // Create blob URL for secure display
        } catch (imageError) {
          console.error('Error fetching cover.png image:', imageError);
        }
      }

      const filteredTracks = response.data.filter(track => track.path_short !== 'cover.png');
      const duration = filteredTracks.reduce((acc, track) => acc + (track.media?.length || 0), 0);

      const tracksWithFullArtUrl = filteredTracks.map((track) => ({
        ...track,
        fullArtUrl: track.media?.art
          ? `https://broadcast.wssr.stream${track.media.art}`
          : null,
      }));

      setTracks(tracksWithFullArtUrl);
      setTopImage(topImageUrl || tracksWithFullArtUrl[0]?.fullArtUrl); // Prioritize cover.png, fallback to first track
      setTrackCount(filteredTracks.length);
      setTotalDuration(duration);
      setHasError(false);
    } catch (error) {
      console.error('Error fetching tracks:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTracks();
  }, [directory]);

  const showSkeleton = loading || (hasError && tracks.length === 0);

  useRetryOnReconnect(fetchTracks, hasError && tracks.length === 0);



  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      if (scrollTop > 100) {
        const newScale = Math.max(0.9, 1 - (scrollTop - 100) / 500);
        setScale(newScale);
      } else {
        setScale(1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const revealObserverCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        revealObserver.current.unobserve(entry.target);
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'translateY(0)';
      }
    });
  };

  useEffect(() => {
    revealObserver.current = new IntersectionObserver(revealObserverCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    });

    tracks.forEach((track) => {
      const element = document.querySelector(`[data-reveal-path="${track.path}"]`);
      if (element) revealObserver.current.observe(element);
    });

    return () => revealObserver.current.disconnect();
  }, [tracks]);

  const handlePlay = async (track, index) => {
    if (isPlaying && currentTrackId === track.path) {
      // Pause playback if the same track is already playing
      setIsPlaying(false);
      return;
    }

    // If the same track is paused, resume playback from the current position
    if (currentTrackId === track.path && !isPlaying) {
      setIsPlaying(true); // Resume playback
      return;
    }

    // Otherwise, start playback for a new track
    setLoadingTrackId(track.path);
    const mediaUrl = track.links?.download
      ? `https://broadcast.wssr.stream${track.links.download}`
      : null;

    if (mediaUrl) {
      try {
        const response = await axios.get(mediaUrl, {
          headers: { Authorization: token },
          responseType: 'blob',
        });

        const blobUrl = URL.createObjectURL(response.data);

        playSpecificTrack(blobUrl, {
          title: track.media?.title || 'Unknown Title',
          artist: track.media?.artist || 'Unknown Artist',
          art: track.media?.art ? `https://broadcast.wssr.stream${track.media.art}` : null,
          duration: track.media?.length || 0,
          trackList: tracks,
          elapsed: currentTrackId === track.path ? nowPlayingData?.elapsed || 0 : 0, // Resume or start fresh
        }, track.path, index);

        // Cache the BLOB URL in the track object
        track.url = blobUrl;

        setIsPlaying(true);
      } catch (error) {
        console.error('Error fetching media file:', error);
      } finally {
        setLoadingTrackId(null);
      }
    } else {
      console.error('Invalid media URL for track:', track);
      setLoadingTrackId(null);
    }
  };








  const directoryShort = directory.split('/').pop();
  const firstTrack = tracks[0];

  return (
    <div>
      {showSkeleton ? (
        <Box sx={{ flexGrow: 1, paddingTop: '80px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
              gap: 3,
              marginBottom: '8rem',
            }}
          >
            {Array.from(new Array(6)).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, gap: 1.5 }}>
                <Box sx={{ flex: { xs: '0 0 42%', sm: '0 0 auto' }, width: { sm: '100%' } }}>
                  <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative' }}>
                    <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 1 }} />
                  </Box>
                </Box>
                <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                  <Skeleton variant="text" width="80%" height={30} />
                  <Skeleton variant="text" width="60%" height={25} sx={{ marginTop: '1rem' }} />
                  <Skeleton variant="text" width="50%" height={20} sx={{ marginTop: '1rem' }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, paddingTop: '80px' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              position: 'fixed',
              top: 80,
              left: { xs: 16, md: `${SIDENAV_WIDTH + 24 + 16}px` },
              zIndex: 10,
              backgroundColor: '#1c232f',
              color: '#fff',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: '#1c232f',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {firstTrack && (
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '20px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
                marginBottom: '1.5rem',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 0,
                }}
              >
                <img
                  src={topImage}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'blur(30px) brightness(0.5)',
                    transform: 'scale(1.15)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.8))',
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  maxWidth: '1200px',
                  margin: '0 auto',
                  padding: { xs: '2rem 1.5rem', md: '3rem 2rem' },
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2, md: 3 },
                  transform: `scale(${scale})`,
                  transformOrigin: 'top',
                  transition: 'transform 0.6s ease, margin 0.6s ease',
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: { xs: '160px', md: '220px' },
                  }}
                >
                  <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative' }}>
                    <img
                      src={topImage}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '4px',
                        boxShadow: '5px 5px 0px 0px rgba(255,102,0,1), 10px 10px 0px 0px rgba(25,35,48,1)',
                      }}
                      alt={directoryShort || 'Art'}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', md: 'flex-start' },
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#fff' }}>
                    {directoryShort}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', marginTop: '0.5rem' }}>
                    {trackCount} {trackCount === 1 ? 'Track' : 'Tracks'} {Bullet} {formatDuration(totalDuration)}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: '999px', marginTop: '1.5rem', paddingX: '1.5rem' }}
                    startIcon={
                      loadingTrackId === firstTrack.path ? (
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                      ) : isPlaying && tracks.some(track => track.path === currentTrackId) ? (
                        <PauseCircleIcon />
                      ) : (
                        <PlayCircleIcon />
                      )
                    }
                    onClick={() => {
                      if (isPlaying && tracks.some(track => track.path === currentTrackId)) {
                        // Pause playback if the current track is from the current list
                        setIsPlaying(false);
                      } else {
                        if (currentTrackId && !tracks.some(track => track.path === currentTrackId)) {
                          // If a track from another list is playing, stop it and start the new list
                          setIsPlaying(false);
                          handlePlay(firstTrack, 0); // Start playback from the new list
                        } else {
                          // If no track is playing or resuming within the same list
                          handlePlay(firstTrack, 0); // Ensure playback starts correctly
                        }
                      }
                    }}
                  >
                    {isPlaying && tracks.some(track => track.path === currentTrackId) ? 'Pause' : 'Play All'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
              gap: 3,
              marginBottom: '8rem',
            }}
          >
            {tracks.map((track, index) => {
              return (
                <Box
                  key={index}
                  data-reveal-path={track.path}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column' },
                    gap: 1.5,
                    opacity: 0,
                    transform: 'translateY(40px)',
                    transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                  }}
                >
                  <Box sx={{ flex: { xs: '0 0 42%', sm: '0 0 auto' }, width: { sm: '100%' } }}>
                    <Box
                      sx={{
                        width: '100%',
                        paddingTop: '100%',
                        position: 'relative',
                      }}
                      onClick={() => handlePlay(track, index)}
                    >
                        {loadingTrackId === track.path && (
                          <LinearProgress
                            variant="buffer"
                            value={progress}
                            valueBuffer={buffer}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              zIndex: 1,
                            }}
                          />
                        )}
                        <img
                          src={track.fullArtUrl}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: '4px',
                          }}
                          alt={track.media?.title || 'Track Art'}
                        />
                        {isPlaying && currentTrackId === track.path ? (
                          <EqualizerIcon
                            fontSize="large"
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              opacity: 0.8,
                              cursor: 'auto',
                            }}
                          />
                        ) : (
                          <PlayArrowSharpIcon
                            fontSize="large"
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              opacity: 0.8,
                              cursor: 'pointer',
                            }}
                          />
                        )}
                        {track.media?.custom_fields?.elink && (
                          <IconButton
                              component="a"
                              href={track.media.custom_fields.elink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(event) => event.stopPropagation()} // Prevent playback on click
                              sx={{
                                clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
                                  position: 'absolute',
                                  bottom: 2,
                                  right: 2,
                                  padding: '8px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: '#FF991C',
                                  borderRadius: '0%',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                      padding: '12px',
                                      clipPath: 'polygon(0 20%, 80% 0, 100% 80%, 20% 100%)',
                                  }
                              }}
                          >
                              <PiSoundcloudLogoDuotone />
                          </IconButton>
                      )}

                      </Box>
                    </Box>
                  <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                      <Typography variant="subtitle1">
                        {track.media?.title || track.path_short}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#999' }}>
                        {track.media?.artist || 'Unknown Artist'}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#999' }}>
                        {formatDuration(track.media?.length || 0)}
                      </Typography>
                      {isPlaying && currentTrackId === track.path && (
                        <Box display="flex" alignItems="center" mt={1}>
                          <EqualizerIcon sx={{ fontSize: '14px', mr: 0.5, color: 'primary.main' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Now Playing
                          </Typography>
                        </Box>
                      )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </div>
  );
};

export default FeedTrackList;
